import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { routingGuidesSchema } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidateTraxAr } from "~/shared/services/api/fetch-client.ts";

const getGlobalRoutingGuides = async (signal?: AbortSignal) => {
	const signalOptions = signal ? { signal } : undefined;
	const resp = await getAndValidateTraxAr(
		apiRoutes.GETGLOBALROUTINGGUIDES,
		routingGuidesSchema.array(),
		signalOptions,
	);

	if (resp.success) {
		return resp.data;
	} else {
		throw new Error(`Failed to fetch global routing guides: ${resp.error}`);
	}
};

export const useGetGlobalRoutingGuides = () => {
	const { showError } = useNotificationActions();
	const {
		data: globalRoutingGuides,
		error: globalRoutingGuidesError,
		isLoading: globalRoutingGuidesIsLoading,
		isFetching: globalRoutingGuidesIsFetching,
		refetch: refetchGlobalRoutingGuides,
	} = useQuery({
		queryKey: ["globalRoutingGuides"],
		queryFn: async ({ signal }: { signal: AbortSignal }) =>
			getGlobalRoutingGuides(signal),
		staleTime: 0,
		gcTime: 0,
		throwOnError: false,
	});

	useEffect(() => {
		if (globalRoutingGuidesError) {
			showError(`${globalRoutingGuidesError.message}`);
		}
	}, [globalRoutingGuidesError, showError]);

	return {
		globalRoutingGuides,
		globalRoutingGuidesError,
		globalRoutingGuidesIsLoading,
		globalRoutingGuidesIsFetching,
		refetchGlobalRoutingGuides,
	};
};
